/**
 * Razorpay Service for frontend
 * Creates Shopify draft orders with Razorpay payment links
 */

import Cookies from 'js-cookie';

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = Cookies.get('crm_auth_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Types - Same format as Shopify draft orders
export interface CreateOrderRequest {
  lead_uuid?: string;
  // Nested customer object (for FloatingOrderPanel)
  customer?: {
    id?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  };
  // Flat customer fields (for simple CreateOrderModal)
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  description?: string;
  notes?: string;
  generate_payment_link?: boolean;
  // Line items (required for Shopify draft orders, optional for simple orders)
  line_items?: Array<{
    variant_id: string;
    quantity: number;
  }>;
  shipping_address?: {
    first_name?: string;
    last_name?: string;
    address1?: string;
    address2?: string;
    city?: string;
    province?: string;
    zip?: string;
    country?: string;
    phone?: string;
  };
  applied_discount?: {
    title?: string;
    description?: string;
    value_type?: string;
    value?: string;
    amount?: string;
  } | null;
  shipping_line?: {
    title?: string;
    price: string;
  } | null;
  tags?: string;
  note?: string;
  // Razorpay specific
  amount?: number; // Optional: custom amount for partial payment (undefined = full order total)
  expire_in_hours?: number;
}

export interface Order {
  id: number;
  uuid: string;
  shopify_order_id: string;
  order_number: string;
  order_name?: string;
  customer_name: string;
  email: string;
  phone: string;
  total_price: string;
  subtotal_price?: string;
  total_discounts?: string;
  currency: string;
  status: string;
  order_type: string;
  source_name?: string;
  line_items?: Array<{
    id: number;
    title: string;
    quantity: number;
    price: string;
    variant_title?: string;
  }>;
  line_items_count?: number;
  created_at: string;
}

export interface PaymentLink {
  payment_link_id: number;
  razorpay_payment_link_id: string;
  short_url: string;
  amount: number;
  status: 'created' | 'sent' | 'partially_paid' | 'paid' | 'cancelled' | 'expired';
  expire_by: string | null;
  whatsapp_sent?: boolean;
  whatsapp_sent_at?: string;
}

export interface CreateOrderResponse {
  success: boolean;
  message: string;
  data?: {
    order: Order;
    payment_link: PaymentLink | null;
  };
  error?: string;
}

export interface OrderListResponse {
  success: boolean;
  data?: {
    orders: Array<Order & {
      latest_payment_link?: {
        short_url: string;
        status: string;
        amount: number;
        whatsapp_sent: boolean;
      };
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      total_pages: number;
    };
  };
}

export interface OrderDetailResponse {
  success: boolean;
  data?: {
    order: Order & {
      financial_status: string;
      fulfillment_status: string;
    };
    payment_links: PaymentLink[];
  };
}

/**
 * Create a Shopify draft order with Razorpay payment link
 * Uses the same payload format as Shopify draft orders
 */
export async function createOrder(request: CreateOrderRequest): Promise<CreateOrderResponse> {
  try {
    const response = await fetch(`${apiUrl}/razorpay/orders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request)
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating order:', error);
    return {
      success: false,
      message: 'Failed to create order',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get order details with payment links
 */
export async function getOrder(orderUuid: string): Promise<OrderDetailResponse> {
  try {
    const response = await fetch(`${apiUrl}/razorpay/orders/${orderUuid}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching order:', error);
    return {
      success: false
    };
  }
}

/**
 * List orders with Razorpay payment links
 */
export async function listOrders(params: {
  page?: number;
  limit?: number;
  status?: string;
  lead_uuid?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
} = {}): Promise<OrderListResponse> {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.lead_uuid) queryParams.append('lead_uuid', params.lead_uuid);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const response = await fetch(`${apiUrl}/razorpay/orders?${queryParams.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error listing orders:', error);
    return {
      success: false
    };
  }
}

/**
 * Generate a new payment link for an existing Shopify order
 */
export async function generatePaymentLink(
  orderUuid: string,
  options: { amount?: number; expire_in_hours?: number } = {}
): Promise<{ success: boolean; data?: PaymentLink; error?: string }> {
  try {
    const response = await fetch(`${apiUrl}/razorpay/orders/${orderUuid}/payment-link`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(options)
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating payment link:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Send payment link via WhatsApp
 */
export async function sendPaymentLinkWhatsApp(
  paymentLinkId: number,
  options: { template_id?: string; custom_message?: string } = {}
): Promise<{ success: boolean; message?: string; data?: { message_id: string; phone: string }; error?: string }> {
  try {
    const response = await fetch(`${apiUrl}/razorpay/payment-links/${paymentLinkId}/send-whatsapp`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(options)
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending WhatsApp:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get orders for a specific lead
 */
export async function getLeadOrders(leadUuid: string): Promise<OrderListResponse> {
  return listOrders({ lead_uuid: leadUuid, limit: 50 });
}

/**
 * Refresh order response interface
 */
export interface RefreshOrderResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    order: {
      order_id: number;
      uuid: string;
      shopify_order_id: string;
      order_number: string;
      order_name: string;
      customer_name: string;
      phone: string;
      email: string;
      total_price: number;
      subtotal_price?: number;
      total_discounts?: number;
      financial_status: string;
      fulfillment_status: string | null;
      order_status: string;
      order_type: string;
      source_name?: string;
      tags: string;
      line_items?: Array<{
        id: number;
        title: string;
        quantity: number;
        price: string;
        variant_title?: string;
      }>;
      line_items_count?: number;
      tracking_info: {
        tracking_numbers: string[];
        tracking_urls: string[];
        tracking_company: string | null;
      } | null;
      updated_at: string;
    };
    payment_link: {
      payment_link_id: number;
      razorpay_payment_link_id: string;
      status: string;
      amount: number;  // In paise
      amount_paid: number;  // In paise
      razorpay_payment_id?: string;
      payment_method?: string;
      short_url?: string;
      paid_at?: string;
    } | null;
  };
}

/**
 * Refresh order status from Shopify
 * Fetches latest fulfillment status, tracking info, etc.
 */
export async function refreshOrderStatus(orderUuid: string): Promise<RefreshOrderResponse> {
  try {
    const response = await fetch(`${apiUrl}/razorpay/orders/${orderUuid}/refresh`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error refreshing order status:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}
